import { createSelector, createSlice } from "@reduxjs/toolkit";

import {
    createSponsor,
    deleteSponsor,
    getAgeLevels,
    getFields,
    getPhotos,
    getPositions,
    getSeasons,
    getSettings,
    getSponsors,
    getUmpires,
    updateSponsor,
} from "./globalActions";

import { authActions } from "../auth/authSlice";

import { RootState } from "../../store";
import {
    IAgeLevel,
    IField,
    IPhoto,
    IPosition,
    ISeason,
    ISiteSetting,
    ISponsor,
    IUser,
} from "../../types/api";
import { IStateDefaults } from "../../types/shared";

export interface GlobalState extends IStateDefaults {
    ageLevels: IAgeLevel[];
    umpires: Array<{ id: IUser["id"]; name: string }>;
    settings: ISiteSetting[];
    fields: IField[];
    seasons: ISeason[];
    positions: IPosition[];
    homePage: {
        selectedYear?: number | undefined;
        selectedSeason?: string | undefined;
        selectedAgeLevel?: string | undefined;
        selectedLeaguePage?: number;
        photos?: IPhoto[];
    };
    sponsors: ISponsor[];
}

const initialState: GlobalState = {
    loading: false,
    success: false,
    ageLevels: [],
    umpires: [],
    settings: [],
    fields: [],
    seasons: [],
    positions: [],
    homePage: {
        selectedYear: undefined,
        selectedAgeLevel: undefined,
        selectedSeason: undefined,
        selectedLeaguePage: 1,
        photos: [],
    },
    sponsors: [],
};

const globalSlice = createSlice({
    name: "global",
    initialState,
    reducers: {
        setSelectedYear: (state, action) => {
            state.homePage.selectedYear = action.payload;
        },
        setSelectedSeason: (state, action) => {
            state.homePage.selectedSeason = action.payload;
        },
        setSelectedAgeLevel: (state, action) => {
            state.homePage.selectedAgeLevel = action.payload;
        },
        setSelectedLeaguePage: (state, action) => {
            state.homePage.selectedLeaguePage = action.payload;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(authActions.logout, state => {
                state.umpires = [];
            })
            .addCase(getAgeLevels.fulfilled, (state, { payload }) => {
                state.ageLevels = payload.ageLevels;
            })
            .addCase(getUmpires.fulfilled, (state, { payload }) => {
                state.umpires = payload.umpires;
            })
            .addCase(getSettings.fulfilled, (state, { payload }) => {
                state.settings = payload;
            })
            .addCase(getFields.fulfilled, (state, { payload }) => {
                state.fields = payload;
            })
            .addCase(getSeasons.fulfilled, (state, { payload }) => {
                state.seasons = payload;
            })
            .addCase(getPositions.fulfilled, (state, { payload }) => {
                state.positions = payload;
            })
            .addCase(getPhotos.fulfilled, (state, { payload }) => {
                state.homePage.photos = payload;
            })
            .addCase(getSponsors.fulfilled, (state, { payload }) => {
                state.sponsors = payload;
            })
            .addCase(updateSponsor.fulfilled, (state, { payload }) => {
                state.sponsors = state.sponsors.map(s => {
                    return s.id === payload.id ? payload : s;
                });
            })
            .addCase(createSponsor.fulfilled, (state, { payload }) => {
                state.sponsors.push(payload);
            })
            .addCase(deleteSponsor.fulfilled, (state, { payload }) => {
                state.sponsors = state.sponsors.filter(s => s.id !== payload);
            });
    },
});

export const globalActions = {
    ...globalSlice.actions,
    getAgeLevels: getAgeLevels,
    getUmpires: getUmpires,
    getSettings: getSettings,
    getFields: getFields,
    getSeasons: getSeasons,
    getPositions: getPositions,
    getPhotos: getPhotos,
    getSponsors: getSponsors,
    updateSponsor: updateSponsor,
    createSponsor: createSponsor,
    deleteSponsor: deleteSponsor,
};

export default globalSlice.reducer;

export const selectAgeLevels = createSelector(
    [(state: RootState) => state.global],
    global => global.ageLevels,
);

export const selectSetting = createSelector(
    [
        (state: RootState) => state.global.settings,
        (state: RootState, settingName: string) => settingName,
    ],
    (settings, settingName) =>
        settings.find(setting => setting.settingName === settingName),
);

export const selectAllSettings = createSelector(
    [(state: RootState) => state.global],
    global => global.settings,
);

export const selectFields = createSelector(
    [(state: RootState) => state.global],
    global => global.fields,
);

export const selectUmpires = createSelector(
    [(state: RootState) => state.global],
    global => global.umpires,
);

export const selectSeasons = createSelector(
    [(state: RootState) => state.global],
    global => global.seasons,
);

export const selectPositions = createSelector(
    [(state: RootState) => state.global],
    global => global.positions,
);

export const selectSponsors = createSelector(
    [(state: RootState) => state.global],
    global => global.sponsors,
);

export const selectSponsor = createSelector(
    [
        (state: RootState) => state.global.sponsors,
        (state: RootState, sponsorId: number) => sponsorId,
    ],
    (sponsors, sponsorId) => sponsors.find(sponsor => sponsor.id === sponsorId),
);
